import { useEffect } from 'react';
import type { SaveOptions } from './use-autosave';

type FlushNow = (options?: SaveOptions) => Promise<void> | void;

export interface UseFlushTriggersArgs {
  flushNow: FlushNow;
  enabled: boolean;
}

/** The tab may be closing, so the save must survive it (NPJB-839 #103). */
export const PAGE_EXIT_SAVE: SaveOptions = { keepalive: true };

interface PageExitTargets {
  doc: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'>;
  win: Pick<Window, 'addEventListener' | 'removeEventListener'>;
}

/**
 * Save the last edit when the page is hidden or closed. Returns the cleanup.
 *
 * The browser cancels a normal request once the tab goes away, so these saves go
 * out with `keepalive`. `pagehide` also covers mobile Safari and the back/forward
 * cache, where `beforeunload` may not fire. Leaving the wizard inside the app is
 * not handled here: that flush stays a normal save (see `useAutosave`).
 */
export function attachPageExitFlush(flushNow: FlushNow, targets?: PageExitTargets): () => void {
  const doc = targets?.doc ?? document;
  const win = targets?.win ?? globalThis;
  const onVisibility = () => {
    if (doc.visibilityState === 'hidden') {
      void flushNow(PAGE_EXIT_SAVE);
    }
  };
  const onPageExit = () => {
    void flushNow(PAGE_EXIT_SAVE);
  };
  doc.addEventListener('visibilitychange', onVisibility);
  win.addEventListener('pagehide', onPageExit);
  win.addEventListener('beforeunload', onPageExit);
  return () => {
    doc.removeEventListener('visibilitychange', onVisibility);
    win.removeEventListener('pagehide', onPageExit);
    win.removeEventListener('beforeunload', onPageExit);
  };
}

export function useFlushTriggers({ flushNow, enabled }: UseFlushTriggersArgs): void {
  useEffect(() => (enabled ? attachPageExitFlush(flushNow) : undefined), [flushNow, enabled]);
}
